<template>
  <div class="is-flex">
    <div class="box">
      <div class="box">
        <div class="title is-4">Information</div>
        <div class="is-flex">
          <div class="container">
            <div class="item">
              <label>Name:</label>
              <input v-model="virtualLobby.name">
            </div>
            <div class="item">
              <label>Path:</label>
              <input v-model="virtualLobby.path">
            </div>
            <div class="colour-picker-container item">
              <label>Background:</label>
              <colour-picker v-model="virtualLobby.backgroundColor"></colour-picker>
            </div>
            <div class="item">
              <label>Language Dropdown:</label>
              <toggle-switch v-model="virtualLobby.languageToolbar"></toggle-switch>
            </div>
          </div>
          <div class="box">
            <div class="toolbar">
              <firebase-upload ref="backgroundImage" :fileName="virtualLobby.name" :fileLocation="'ClickableAreas'" @imageURL="setImage"></firebase-upload>
            </div>
          </div>
        </div>
      </div>
      <div class="konva-container">
        <v-stage ref="stage" class="canvas-container" :config='configKonva'   @mousedown="handleStageMouseDown"
        @touchstart="handleStageMouseDown">
          <v-layer ref="layer">
            <v-rect
              v-for="item in rectangles"
              :key="item.id"
              :config="item"
              @transformend="handleTransformEnd"
              @dragend="handleTransformEnd"
            />
            <v-transformer ref="transformer" />
          </v-layer>
        </v-stage>
        <div :style="cssVars" class="overlay-konva">
        </div>
      </div>
      <modal-create-area :rectIndex="rectIndex" ref="createAreaModal" @rectangleParams="receiveRectangleParams"></modal-create-area>
      <div class="tabs is-toggle">
        <ul>
          <li @click="showCreateAreaModal()">
            <a>
              <span class="icon is-small"><i class="fas fa-clone" aria-hidden="true"></i></span>
              <span>New Area</span>
            </a>
          </li>
          <li @click="save()">
            <a>
              <span class="icon is-small"><i class="fas fa-save" aria-hidden="true"></i></span>
              <span>Save</span>
            </a>
          </li>
        </ul>
      </div>
    </div>
    <div v-if="rectangles[0]" class="box clickable-areas">
      <h1>Clickables Areas:</h1>
      <div class="clickable-area box" v-for="(rectangle, index) in rectangles" :key="index" :class="{selected : selectedShapeName === rectangle.name}">
        <label>Name:</label>
        <h1 v-if="!editMode">{{rectangle.label}}</h1>
        <input v-if="editMode" v-model="rectangle.label">
        <label>Resource:</label>
        <h1 v-if="!editMode">{{rectangle.url}}</h1>
        <input v-if="editMode" v-model="rectangle.url">
        <i v-if="!editMode" @click="handleEditMode" class="fas fa-edit"></i>
        <i v-if="editMode" @click="handleEditMode" class="far fa-save"></i>
      </div>
    </div>
  </div>
</template>

<script>
import Vue from 'vue'
import VueKonva from 'vue-konva'
import ImageUpload from '@/components/Inputs/ImageUpload'
import ModalCreateArea from '@/components/Modals/CreateClickableAreas'
import ColourPicker from '@/components/MagicBoard/palettes/ColourPicker'
import ToggleSwitch from '@/components/Controls/ToggleSwitch'
import virtualLobby from '@/api/virtual-lobby'
import uploadMixin from '../imageUploadMixin'
import FirebaseUpload from '@/components/Forms/FirebaseUpload'

Vue.use(VueKonva)

const konvaHeight = 600
const konvaWidth = 800

export default {
  name: 'Create',
  mixins: [uploadMixin],
  components: {
    ImageUpload,
    ModalCreateArea,
    ColourPicker,
    ToggleSwitch,
    FirebaseUpload
  },
  props: ['existingLobby'],
  data: function () {
    return {
      configKonva: {
        width: 800,
        height: 600
      },
      newImage: '',
      image: null,
      clickableAreas: [],
      rectangles: [],
      selectedShapeName: '',
      rectIndex: 0,
      virtualLobby: {
        backgroundColor: '#000000',
        name: 'default',
        url: '',
        path: '',
        languageToolbar: false,
        clickableAreas: []
      },
      editMode: false,
      updateMode: false
    }
  },
  mounted: function () {
    if (this.existingLobby) {
      this.getExistingLobbyInfo()
      this.updateMode = true
    }
  },
  computed: {
    cssVars: function () {
      return {
        '--background': 'url(' + this.virtualLobby.url + ')' + 'no-repeat'
      }
    }
  },
  methods: {
    setImage: function (value) {
      this.virtualLobby.url = value
    },
    handleTransformEnd: function (e) {
      // shape is transformed, let us save new attrs back to the node
      // find element in our state
      const rect = this.rectangles.find(
        (r) => r.name === this.selectedShapeName
      )
      // update the state
      rect.x = e.target.x()
      rect.y = e.target.y()
      rect.rotation = e.target.rotation()
      rect.scaleX = e.target.scaleX()
      rect.scaleY = e.target.scaleY()
    },
    handleStageMouseDown: function (e) {
      // clicked on stage - clear selection
      if (e.target === e.target.getStage()) {
        this.selectedShapeName = ''
        this.updateTransformer()
        return
      }

      // clicked on transformer - do nothing
      const clickedOnTransformer =
        e.target.getParent().className === 'Transformer'
      if (clickedOnTransformer) {
        return
      }

      // find clicked rect by its name
      const name = e.target.name()
      const rect = this.rectangles.find((r) => r.name === name)
      if (rect) {
        this.selectedShapeName = name
      } else {
        this.selectedShapeName = ''
      }
      this.updateTransformer()
    },
    updateTransformer: function () {
      // here we need to manually attach or detach Transformer node
      const transformerNode = this.$refs.transformer.getNode()
      const stage = transformerNode.getStage()
      const { selectedShapeName } = this

      const selectedNode = stage.findOne('.' + selectedShapeName)
      // do nothing if selected node is already attached
      if (selectedNode === transformerNode.node()) {
        return
      }

      if (selectedNode) {
        // attach to another node
        transformerNode.nodes([selectedNode])
      } else {
        // remove transformer
        transformerNode.nodes([])
      }
    },
    createRectangle: function (value) {
      this.rectangles.push(
        {
          id: 0,
          rotation: 0,
          x: 10,
          y: 10,
          width: 100,
          height: 100,
          scaleX: 1,
          scaleY: 1,
          fill: 'lightgrey',
          name: 'rect' + this.rectIndex++,
          label: value.label,
          opacity: 0.5,
          draggable: true,
          url: value.url,
          isVideo: value.false
        }
      )
    },
    showCreateAreaModal: function () {
      this.$refs.createAreaModal.active = true
    },
    receiveRectangleParams: function (value) {
      this.createRectangle(value)
    },
    save: function () {
      this.calculatePorcentages()
    },
    calculatePorcentages: function () {
      this.rectangles = this.rectangles.forEach((rectangle, index) => {
        this.virtualLobby.clickableAreas[index] = { shape: {} }
        this.virtualLobby.clickableAreas[index].name = rectangle.name
        this.virtualLobby.clickableAreas[index].id = rectangle.id
        this.virtualLobby.clickableAreas[index].label = rectangle.label
        this.virtualLobby.clickableAreas[index].resourceType = rectangle.isVideo ? 'video' : 'link'
        this.virtualLobby.clickableAreas[index].url = rectangle.url
        this.virtualLobby.clickableAreas[index].shape.height = (((rectangle.scaleY * 100) * 100) / konvaHeight).toString() + '%'
        this.virtualLobby.clickableAreas[index].shape.width = ((rectangle.scaleX * 100) * 100 / konvaWidth).toString() + '%'
        this.virtualLobby.clickableAreas[index].shape.top = ((rectangle.y * 100) / konvaHeight).toString() + '%'
        this.virtualLobby.clickableAreas[index].shape.left = ((rectangle.x * 100) / konvaWidth).toString() + '%'
      })
      if (!this.updateMode) {
        virtualLobby.createVirtualLobby(this.virtualLobby).then((response) => {
          this.$router.push({ name: 'lobbyView' })
        }, (error) => {
          console.error(error)
        })
      } else {
        virtualLobby.updateVirtualLobby(this.virtualLobby).then((response) => {
          this.$router.push({ name: 'lobbyView' })
        }, (error) => {
          console.error(error)
        })
      }
    },
    handleEditMode: function () {
      this.editMode = !this.editMode
    },
    getExistingLobbyInfo: function () {
      this.virtualLobby.backgroundColor = this.existingLobby.backgroundColor
      this.virtualLobby.languageToolbar = this.existingLobby.languageToolbar
      this.virtualLobby.path = this.existingLobby.path
      this.virtualLobby.name = this.existingLobby.name
      this.virtualLobby.url = this.existingLobby.url
      this.rectIndex = this.existingLobby.clickableareas?.length
      this.existingLobby.clickableareas.forEach((area, index) => {
        this.rectangles.push(
          {
            id: area.id,
            rotation: 0,
            x: (parseInt(area.shape.left.replace('%', '')) * 800) / 100,
            y: (parseInt(area.shape.top.replace('%', '')) * 600) / 100,
            width: 100,
            height: 100,
            scaleX: ((parseInt(area.shape.width) * konvaWidth) / 100) / 100,
            scaleY: ((parseInt(area.shape.height) * konvaHeight) / 100) / 100,
            fill: 'lightgrey',
            name: area.name,
            label: area.label,
            opacity: 0.5,
            draggable: true,
            url: area.url,
            isVideo: area.resourceType === 'link' ? 'link' : 'video'
          }
        )
      })
    }
  }
}
</script>

<style lang="scss" scoped>

.canvas-container{
  border: 1px solid black;
}

.konva-container {
  position: relative;
}

.overlay-konva {
  position: absolute;
  top: 0;
  right: 0;
  left: 0;
  bottom: 0;
  background: var(--background);
  background-size: 100% 100%;
  background-position: 50% 50%;
}

::v-deep .konvajs-content {
  z-index: 1;
}

.colour-picker-container {
  width: 19rem;
  display: flex;
}

.item {
  margin-bottom: 1rem;
}

.clickable-areas {
  height: auto;
  align-self: center;
  margin-left: 2rem;
}

.selected {
  border: red 1px solid;
}

.fa-edit {
  position: absolute;
  right: 0;
  top: 0;
  margin: 0.5rem;
  cursor: pointer;
  &:hover {
    font-size: 1rem;
    color: red;
  }
}

.clickable-area .fa-save {
  position: absolute;
  right: 0;
  top: 0;
  margin: 0.5rem;
  cursor: pointer;
  &:hover {
    font-size: 1rem;
    color: red;
  }
}
.clickable-area {
  position: relative;
  width: 12rem;
  label {
    font-weight: bold;
  }
}

</style>
